import { alpha, createTheme } from "@mui/material";

import { TypographyVariantsOptions } from "@mui/material/styles/createTypography";

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    body2bold: true;
    filterTypeTitleMini: true;
    filterTypeSubtitleMini: true;
    nudgeSubtitleMini: true;
    nudgeSubtitleMini2: true;
    detailHeaderTitle: true;
    detailCellTitle: true;
    detailCellBody: true;
    detailCellSubtitle: true;
    detailEntryTitle: true;
    detailEntryBody: true;
    clinicianInitials: true;
    notesTitle: true;
    notesSubtitle: true;
    notesBody: true;
    errorBody: true;
    tooltip: true;
  }
}

interface ExtendedTypographyOptions extends TypographyVariantsOptions {
  body2bold: React.CSSProperties;
  filterTypeTitleMini: React.CSSProperties;
  filterTypeSubtitleMini: React.CSSProperties;
  nudgeSubtitleMini: React.CSSProperties;
  nudgeSubtitleMini2: React.CSSProperties;
  detailHeaderTitle: React.CSSProperties;
  detailCellTitle: React.CSSProperties;
  detailCellBody: React.CSSProperties;
  detailCellSubtitle: React.CSSProperties;
  detailEntryTitle: React.CSSProperties;
  detailEntryBody: React.CSSProperties;
  clinicianInitials: React.CSSProperties;
  notesTitle: React.CSSProperties;
  notesSubtitle: React.CSSProperties;
  notesBody: React.CSSProperties;
  errorBody: React.CSSProperties;
  tooltip: React.CSSProperties;
}

declare module "@mui/material/styles" {
  interface Palette {
    detailBackground: Palette["primary"];
  }

  interface PaletteOptions {
    detailBackground?: PaletteOptions["primary"];
  }

  interface Palette {
    newMessageDot: Palette["primary"];
  }

  interface PaletteOptions {
    newMessageDot?: PaletteOptions["primary"];
  }

  interface Palette {
    flagForSupervisor: Palette["primary"];
  }

  interface PaletteOptions {
    flagForSupervisor?: PaletteOptions["primary"];
  }

  interface BreakpointOverrides {
    md: false;
    xl: false;
    desktop: true;
  }
}

const initTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      lg: 1200,
      desktop: 1360,
    },
  },
  palette: {
    detailBackground: {
      main: "#f3f8fb",
    },
    newMessageDot: {
      main: "#52a66b",
    },
    flagForSupervisor: {
      main: "#b11a1a",
    },
  },
});

export const theme = createTheme(initTheme, {
  typography: {
    h1: {
      fontSize: 48,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 43,
      },
    },
    h2: {
      fontSize: 40,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 36,
      },
    },
    h3: {
      fontSize: 32,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 29,
      },
    },
    h4: {
      fontSize: 28,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 25,
      },
    },
    h5: {
      fontSize: 22,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 20,
      },
    },
    h6: {
      fontSize: 18,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 16,
      },
    },
    body2bold: {
      ...initTheme.typography.body2,
      fontWeight: 500,
    },
    filterTypeTitleMini: {
      ...initTheme.typography.body1,
      color: initTheme.palette.primary.main,
      fontWeight: 500,
      fontSize: 18,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 16,
      },
    },
    filterTypeSubtitleMini: {
      ...initTheme.typography.body1,
      color: initTheme.palette.text.secondary,
      fontWeight: 500,
      fontSize: 8,
      borderRadius: 1000,
      backgroundColor: alpha(initTheme.palette.common.black, 0.08),
      textAlign: "center",
      textTransform: "uppercase",
      lineHeight: 2.5,
      width: initTheme.spacing(7),
      marginBottom: initTheme.spacing(2),
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 7,
        width: initTheme.spacing(6),
      },
    },
    nudgeSubtitleMini: {
      ...initTheme.typography.body1,
      color: initTheme.palette.text.secondary,
      fontWeight: 500,
      fontSize: 10,
      textAlign: "center",
      textTransform: "uppercase",
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 9,
      },
    },
    nudgeSubtitleMini2: {
      ...initTheme.typography.body1,
      color: initTheme.palette.text.secondary,
      fontWeight: 400,
      fontSize: 8,
      textAlign: "center",
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 7,
      },
    },
    detailHeaderTitle: {
      ...initTheme.typography.h5,
      color: initTheme.palette.primary.main,
      fontWeight: "bold",
      fontSize: 22,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 20,
      },
    },
    detailCellTitle: {
      ...initTheme.typography.body1,
      color: initTheme.palette.grey[800],
      fontWeight: 400,
      fontSize: 12,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 11,
      },
    },
    detailCellBody: {
      ...initTheme.typography.body1,
      fontWeight: 500,
      fontSize: 14,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 13,
      },
    },
    detailCellSubtitle: {
      ...initTheme.typography.body1,
      color: initTheme.palette.grey[800],
      fontWeight: 400,
      fontSize: 13,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 12,
      },
      fontStyle: "italic",
    },
    detailEntryTitle: {
      ...initTheme.typography.h6,
      color: initTheme.palette.text.secondary,
      fontWeight: 500,
      fontSize: 18,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 16,
      },
    },
    detailEntryBody: {
      ...initTheme.typography.subtitle1,
      fontWeight: 400,
      fontSize: 14,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 13,
      },
    },
    clinicianInitials: {
      ...initTheme.typography.body1,
      color: initTheme.palette.common.white,
      fontWeight: 500,
      fontSize: 14,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 13,
      },
    },
    notesTitle: {
      ...initTheme.typography.body1,
      fontWeight: 500,
      fontSize: 18,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 16,
      },
    },
    notesSubtitle: {
      ...initTheme.typography.body1,
      fontWeight: 400,
      fontSize: 12,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 11,
      },
    },
    notesBody: {
      ...initTheme.typography.body1,
      fontWeight: 400,
      fontSize: 15,
      [initTheme.breakpoints.down("desktop")]: {
        fontSize: 13,
      },
      whiteSpace: "pre-wrap",
    },
    errorBody: {
      ...initTheme.typography.subtitle1,
      color: initTheme.palette.error.main,
    },
    tooltip: {
      // From React MUI's tooltip.js file
      fontFamily: initTheme.typography.fontFamily,
      fontSize: initTheme.typography.pxToRem(11),
      maxWidth: 300,
      wordWrap: "break-word",
      fontWeight: initTheme.typography.fontWeightMedium,
    },
  } as ExtendedTypographyOptions,
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          /* Applied to ALL buttons default 'font-size=14px'*/
          [initTheme.breakpoints.down("desktop")]: {
            fontSize: "13px",
          },
        },
        sizeSmall: {
          /* Applied ONLY to size="small" buttons default 'font-size=13px'*/
          [initTheme.breakpoints.down("desktop")]: {
            fontSize: "12px",
          },
        },
        sizeMedium: {
          /* Applied ONLY to size="medium" buttons default 'font-size=14px'*/
          [initTheme.breakpoints.down("desktop")]: {
            fontSize: "13px",
          },
        },
        sizeLarge: {
          /* Applied ONLY to size="large" buttons default 'font-size=15px' */
          [initTheme.breakpoints.down("desktop")]: {
            fontSize: "13px",
          },
        },
      },
    },
  },
});
